import { ReactNode, useId } from "react";
import { Mui, useTheme } from "@leap4/frontend";
import { seriesColors } from "../../../theme/colors";

const { Box, CircularProgress, Typography } = Mui;

const BARS = [52, 64, 58, 72, 68, 80, 76, 70, 84, 78, 88, 82, 74, 90];
const BAR_W = 26;
const GAP = 600 / BARS.length;
const LINE = BARS.map(
  (h, i) => `${i ? "L" : "M"}${i * GAP + GAP / 2},${170 - h * 1.2 - 20}`,
).join(" ");

/** The dashboard chart while its first range loads: bars rise and the trend draws. */
export const DashboardLoadingChart = ({ height, label = "Loading performance…", spinner }: { height: number; label?: ReactNode; spinner?: boolean }) => {
  const theme = useTheme();
  const id = useId().replace(/:/g, "");
  return (
    <Box
      aria-busy="true"
      sx={{ height, display: "flex", flexDirection: "column" }}
    >
      <Box sx={{ flex: 1, minHeight: 0, position: "relative" }}>
        {spinner && (
          <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", pointerEvents: "none" }}>
            <Box sx={{ display: "grid", placeItems: "center", width: 64, height: 64, borderRadius: "50%", bgcolor: "background.paper", boxShadow: 3 }}>
              <CircularProgress size={36} thickness={4} />
            </Box>
          </Box>
        )}
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 600 180"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <style>{`
                        .dl-bar-${id} { transform-box: fill-box; transform-origin: bottom; }
                        .dl-all-${id} { animation: dl-all-${id} 3.2s linear infinite; }
                        @keyframes dl-all-${id} { 0%, 88% { opacity: 1; } 100% { opacity: 0; } }
                        ${BARS.map((_, i) => `.dl-bar-${id}.b${i} { animation: dl-bar-${id}-${i} 3.2s ease-out infinite; } @keyframes dl-bar-${id}-${i} { 0%, ${i * 1.5}% { transform: scaleY(0); } ${i * 1.5 + 18}%, 100% { transform: scaleY(1); } }`).join("\n")}
                        .dl-line-${id} { transform-box: fill-box; transform-origin: left; animation: dl-line-${id} 3.2s ease-out infinite; }
                        @keyframes dl-line-${id} { 0%, 30% { transform: scaleX(0); } 75%, 100% { transform: scaleX(1); } }
                        @media (prefers-reduced-motion: reduce) { .dl-bar-${id}, .dl-line-${id}, .dl-all-${id} { animation: none !important; } }
                    `}</style>
          <defs>
            <clipPath id={`dl-clip-${id}`}>
              <rect
                className={`dl-line-${id}`}
                x="0"
                y="0"
                width="600"
                height="180"
              />
            </clipPath>
          </defs>
          {[40, 85, 130].map((y) => (
            <line
              key={y}
              x1="0"
              x2="600"
              y1={y}
              y2={y}
              stroke={theme.palette.divider}
              strokeDasharray="4 4"
              vectorEffect="non-scaling-stroke"
            />
          ))}
          <line
            x1="0"
            x2="600"
            y1="170"
            y2="170"
            stroke={theme.palette.divider}
            vectorEffect="non-scaling-stroke"
          />
          <g className={`dl-all-${id}`}>
            {BARS.map((h, i) => (
              <rect
                key={i}
                className={`dl-bar-${id} b${i}`}
                x={i * GAP + (GAP - BAR_W) / 2}
                y={170 - h * 1.6}
                width={BAR_W}
                height={h * 1.6}
                rx="3"
                fill={seriesColors.impressions}
                fillOpacity={0.45}
              />
            ))}
            <g clipPath={`url(#dl-clip-${id})`}>
              <path
                d={LINE}
                fill="none"
                stroke={seriesColors.visits}
                strokeWidth={2.5}
                vectorEffect="non-scaling-stroke"
              />
            </g>
          </g>
        </svg>
      </Box>
      <Box
        role="status"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          pt: 1.5,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>
    </Box>
  );
};
